import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lightning, CheckCircle, CurrencyBtc, ArrowRight } from "@phosphor-icons/react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import client from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";

// One-time PayPal (used for shop products)
const PayPalOneTime = ({ order, onSuccess }) => {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { client.get("/payments/paypal/config").then(r => setCfg(r.data)); }, []);
  if (!cfg) return <div className="font-mono-k text-[#A1A1AA] text-sm">Loading PayPal…</div>;
  if (!cfg.enabled) return <div className="font-mono-k text-red-400 text-sm">PayPal not configured.</div>;

  return (
    <PayPalScriptProvider options={{
      "client-id": cfg.client_id,
      currency: "USD",
      intent: "capture",
      "enable-funding": "card,venmo,paylater",
      "disable-funding": "",
      components: "buttons",
    }}>
      <div className="bg-white p-3" data-testid="paypal-onetime-wrapper">
        <PayPalButtons
          fundingSource={undefined}
          style={{ layout: "vertical", color: "gold", shape: "rect", label: "pay", height: 45 }}
          createOrder={(_, actions) =>
            actions.order.create({
              intent: "CAPTURE",
              purchase_units: [{
                reference_id: order.id,
                description: order.label,
                amount: { currency_code: "USD", value: order.amount.toFixed(2) },
              }],
              application_context: {
                shipping_preference: "NO_SHIPPING",
                user_action: "PAY_NOW",
                brand_name: "Korporate",
              },
            })
          }
          onApprove={async (data, actions) => {
            try {
              const details = await actions.order.capture();
              await client.post(`/payments/paypal/capture/${order.id}`, {
                paypal_order_id: data.orderID,
                payer_id: data.payerID || details?.payer?.payer_id || "",
                capture_status: details?.status || "COMPLETED",
              });
              onSuccess();
            } catch (err) {
              console.error("PayPal capture failed:", err);
              toast.error(err?.response?.data?.detail || err?.message || "Capture failed");
            }
          }}
          onError={(err) => {
            console.error("PayPal button error:", err);
            const msg = err?.message || (typeof err === "string" ? err : "Payment could not be processed. Try another card or PayPal account.");
            toast.error(msg);
          }}
          onCancel={() => toast.message("PayPal payment cancelled")}
        />
      </div>
    </PayPalScriptProvider>
  );
};

// Recurring PayPal Subscription (used for Pro)
const PayPalSubscription = ({ order, onSuccess }) => {
  const [cfg, setCfg] = useState(null);
  const [plan, setPlan] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    (async () => {
      try {
        const { data: c } = await client.get("/payments/paypal/config");
        setCfg(c);
        if (!c.subscription_enabled) { setErr("PayPal subscriptions not configured (missing secret)."); return; }
        const tier = order?.kind === "boss" ? "boss" : "pro";
        const { data: p } = await client.get(`/payments/paypal/plan?tier=${tier}`);
        setPlan(p);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Failed to load plan");
      }
    })();
  }, [order?.kind]);
  if (err) return <div className="font-mono-k text-red-400 text-sm">{err}</div>;
  if (!cfg || !plan) return <div className="font-mono-k text-[#A1A1AA] text-sm">Loading subscription…</div>;

  return (
    <PayPalScriptProvider options={{
      "client-id": cfg.client_id,
      vault: true,
      intent: "subscription",
      currency: "USD",
    }}>
      <div className="bg-white p-3" data-testid="paypal-subscribe-wrapper">
        <PayPalButtons
          style={{ layout: "vertical", color: "gold", shape: "rect", label: "subscribe", height: 45 }}
          createSubscription={(_, actions) => actions.subscription.create({ plan_id: plan.plan_id })}
          onApprove={async (data) => {
            try {
              await client.post(`/payments/paypal/subscription/activate`, {
                subscription_id: data.subscriptionID,
              });
              // Also mark the initiating order as paid for records
              try { await client.post(`/payments/confirm/${order.id}`); } catch (err) { if (process.env.NODE_ENV !== "production") console.warn("confirm sync failed:", err); }
              onSuccess();
            } catch (e) { toast.error(e?.response?.data?.detail || "Activation failed"); }
          }}
          onError={(err) => { toast.error("PayPal error"); console.error(err); }}
          onCancel={() => toast.message("Subscription cancelled")}
        />
      </div>
    </PayPalScriptProvider>
  );
};

const Checkout = () => {
  const { orderId } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState("review");
  const [delivery, setDelivery] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const loadOrder = () => client.get("/orders").then(r => {
      if (cancelled) return;
      const o = r.data.find(x => x.id === orderId);
      if (!o) { toast.error("Order not found"); nav("/dashboard"); return; }
      setOrder(o);
      if (o.status === "paid") setStatus("done");
    });
    loadOrder();

    // If returning from NOWPayments, poll for confirmation (webhook fulfills async)
    const params = new URLSearchParams(window.location.search);
    if (params.get("paid") === "1") {
      setStatus("processing");
      const poll = setInterval(loadOrder, 3000);
      const timeout = setTimeout(() => { clearInterval(poll); setStatus("review"); toast.message("Still waiting on crypto confirmation. Refresh in a few minutes."); }, 5 * 60_000);
      return () => { cancelled = true; clearInterval(poll); clearTimeout(timeout); };
    }
    return () => { cancelled = true; };
  }, [orderId, nav]);

  // Load delivery info once paid + product kind
  useEffect(() => {
    if (status === "done" && order?.kind === "product") {
      client.get(`/orders/${orderId}/delivery`).then(r => setDelivery(r.data)).catch(() => {});
    }
  }, [status, order, orderId]);

  const [payError, setPayError] = useState("");

  const payCrypto = async () => {
    setStatus("processing");
    setPayError("");
    try {
      if (order.provider === "nowpayments") {
        // Real hosted checkout via NOWPayments
        const { data } = await client.post("/payments/crypto/create-invoice", {
          order_id: orderId,
          pay_currency: "usdttrc20", // default USDT-TRC20
        });
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
          return;
        }
        throw new Error("No checkout URL returned");
      }
      // Legacy sandbox path
      await new Promise(r => setTimeout(r, 1200));
      await client.post(`/payments/confirm/${orderId}`);
      setStatus("done");
      toast.success("Payment confirmed");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed";
      toast.error(msg);
      setPayError(msg);
      setStatus("review");
    }
  };

  const onSuccess = () => { setStatus("done"); toast.success("Success"); };

  if (!order) return <div className="min-h-screen bg-[#0A0D14]" />;

  const isSubscription = (order.kind === "pro" || order.kind === "boss") && order.provider === "paypal";
  const isPayPalOneTime = order.kind === "product" && order.provider === "paypal";
  const isPayPalTrial = order.kind === "trial" && order.provider === "paypal";
  const isCrypto = order.provider === "nowpayments" || order.provider === "stripe_crypto";

  return (
    <div className="min-h-screen bg-[#0A0D14] text-white flex items-center justify-center noise p-6">
      <div className="w-full max-w-lg">
        <div className="flex items-center gap-2 mb-8">
          <BrandLogo height={56} />
        </div>

        <div className="border border-white/10 bg-[#111318] p-8 brutal-shadow">
          <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-3">
            — {isSubscription ? "PAYPAL SUBSCRIPTION" : isPayPalOneTime ? "PAYPAL CHECKOUT" : isPayPalTrial ? "PAYPAL TRIAL" : order.provider === "nowpayments" ? "CRYPTO CHECKOUT · USDT" : "STRIPE CRYPTO CHECKOUT"}
          </div>
          <div className="font-display text-3xl tracking-tighter mb-2">{order.label}</div>
          <div className="font-mono-k text-4xl text-[#FACC15]" data-testid="checkout-amount">${order.amount.toFixed(2)}</div>
          {isSubscription && <div className="font-mono-k text-xs text-[#A1A1AA] mb-6">Billed monthly · Cancel anytime</div>}
          {!isSubscription && <div className="mb-6" />}

          {status === "review" && isSubscription && (
            <div>
              <p className="text-sm font-mono-k text-[#A1A1AA] mb-4">
                Subscribe with PayPal. Your card will be charged ${order.amount.toFixed(2)} today and every 30 days until you cancel.
              </p>
              <PayPalSubscription order={order} onSuccess={onSuccess} />
              <button onClick={() => nav(-1)} className="k-btn w-full justify-center mt-4" data-testid="checkout-cancel">CANCEL</button>
            </div>
          )}

          {status === "review" && isPayPalOneTime && (
            <div>
              <p className="text-sm font-mono-k text-[#A1A1AA] mb-4">Complete your one-time purchase with PayPal.</p>
              <PayPalOneTime order={order} onSuccess={onSuccess} />
              <button onClick={() => nav(-1)} className="k-btn w-full justify-center mt-4" data-testid="checkout-cancel">CANCEL</button>
            </div>
          )}

          {status === "review" && isCrypto && (
            <div>
              {order.provider === "nowpayments" ? (
                <p className="text-sm font-mono-k text-[#A1A1AA] mb-6">
                  Pay ${order.amount.toFixed(2)} with USDT (TRC20 network by default) via NOWPayments hosted checkout.<br /><br />
                  You'll be redirected to a secure crypto checkout — send USDT from any wallet (Trust, MetaMask, exchange). Your Pro access unlocks automatically once the transaction confirms on-chain.
                </p>
              ) : (
                <p className="text-sm font-mono-k text-[#A1A1AA] mb-6">
                  You'll be redirected to Stripe to pay with your card or supported stablecoin (USDC).<br /><br />
                  <span className="text-[#FACC15]">SANDBOX MODE</span> — click confirm to simulate a successful transaction.
                </p>
              )}
              <button onClick={payCrypto} className="k-btn primary w-full justify-center mb-3" data-testid="checkout-pay">
                <CurrencyBtc size={18} /> {order.provider === "nowpayments" ? "CONTINUE TO USDT PAYMENT" : "CONFIRM PAYMENT"} <ArrowRight size={16} />
              </button>
              {payError && (
                <div className="border border-red-500/40 bg-red-500/10 text-red-300 text-xs font-mono-k p-3 mb-3" data-testid="checkout-error">
                  {payError}
                </div>
              )}
              <button onClick={() => nav(-1)} className="k-btn w-full justify-center" data-testid="checkout-cancel">CANCEL</button>
            </div>
          )}

          {status === "processing" && (
            <div className="text-center py-4"><div className="font-mono-k text-[#A1A1AA]">Processing…</div></div>
          )}

          {status === "done" && (
            <div className="text-center py-4">
              <CheckCircle size={64} weight="duotone" className="text-[#FACC15] mx-auto mb-4" />
              <div className="font-display text-2xl mb-2">{isSubscription ? "Subscription active." : "Payment complete."}</div>
              <div className="text-sm font-mono-k text-[#A1A1AA] mb-6">A receipt has been emailed to you.</div>

              {delivery && (delivery.delivery_url || delivery.license_key || delivery.delivery_note) && (
                <div className="text-left border border-[#FACC15] bg-[#0A0D14] p-4 mb-6" data-testid="delivery-block">
                  <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-3">— YOUR DELIVERY</div>
                  {delivery.delivery_url && (
                    <div className="mb-3">
                      <div className="text-[10px] font-mono-k tracking-widest-k text-[#A1A1AA] mb-1">ACCESS URL</div>
                      <a href={delivery.delivery_url} target="_blank" rel="noreferrer" className="font-mono-k text-sm text-[#FACC15] break-all hover:underline" data-testid="delivery-url">{delivery.delivery_url}</a>
                    </div>
                  )}
                  {delivery.license_key && (
                    <div className="mb-3">
                      <div className="text-[10px] font-mono-k tracking-widest-k text-[#A1A1AA] mb-1">LICENSE KEY</div>
                      <div className="font-mono-k text-sm text-white bg-[#111318] p-2 border border-white/10 break-all" data-testid="delivery-key">{delivery.license_key}</div>
                    </div>
                  )}
                  {delivery.delivery_note && (
                    <div>
                      <div className="text-[10px] font-mono-k tracking-widest-k text-[#A1A1AA] mb-1">NOTE</div>
                      <div className="font-mono-k text-xs text-white whitespace-pre-wrap" data-testid="delivery-note">{delivery.delivery_note}</div>
                    </div>
                  )}
                </div>
              )}

              <button onClick={() => nav("/dashboard")} className="k-btn primary w-full justify-center" data-testid="checkout-back">BACK TO DASHBOARD <ArrowRight size={16} /></button>
            </div>
          )}
        </div>

        <div className="mt-4 text-center text-[10px] font-mono-k tracking-widest-k text-[#A1A1AA]">
          {order.provider === "paypal" ? "PAYPAL SECURE CHECKOUT · Cards accepted" : order.provider === "nowpayments" ? "NOWPAYMENTS · USDT / BTC / ETH / USDC" : "STRIPE CRYPTO"}
        </div>
      </div>
    </div>
  );
};

export default Checkout;
